import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import AudioPipeline from './audio/AudioPipeline';

const LANGUAGES = [
  { code: 'zh', name: '中文' },
  { code: 'en', name: 'English' },
  { code: 'ja', name: '日本語' }
];

const MODELS = [
  { id: 'gpt-5.1', name: 'GPT-5.1 (高质量)' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini (推荐)' }
];

const API_BASE = process.env.NODE_ENV === 'development'
  ? `http://${window.location.hostname}:8000`
  : '';

function App() {
  const [view, setView] = useState('record'); // 'record' | 'history' | 'playback'
  const [isRecording, setIsRecording] = useState(false);
  const [sourceLang, setSourceLang] = useState('zh');
  const [targetLang, setTargetLang] = useState('en');
  const [model, setModel] = useState('gpt-4o-mini');
  const [results, setResults] = useState([]);
  const [interimText, setInterimText] = useState('');
  const [interimTranslated, setInterimTranslated] = useState('');
  const [currentResult, setCurrentResult] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle');
  const [usage, setUsage] = useState({ totalTokens: 0, totalCost: 0, whisperSeconds: 0 });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [pipelineStats, setPipelineStats] = useState(null);

  // 音频管线预加载状态
  // 'loading' | 'ready' | 'fallback'（降级：不使用降噪/VAD）
  const [pipelineState, setPipelineState] = useState('loading');
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 9, stage: '' });
  const pipelineCapRef = useRef({ denoise: false, vad: false }); // 各模块可用性

  // 录音保存相关
  const [recordings, setRecordings] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(-1);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]); // 完整音频数据
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const subtitlesRef = useRef([]); // 字幕数据
  const recordingStartTimeRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const pipelineRef = useRef(null); // 经典模式音频管线 (降噪 + VAD)

  // 加载历史录音列表
  const loadRecordings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/recordings/list`);
      const data = await res.json();
      setRecordings(data.recordings || []);
    } catch (err) {
      console.error('Failed to load recordings:', err);
    }
  }, []);

  useEffect(() => {
    loadRecordings();
  }, [loadRecordings]);

  // 页面加载时预加载音频管线资源
  useEffect(() => {
    let cancelled = false;

    // 3 分钟超时降级
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) {
        setPipelineState(prev => {
          if (prev === 'loading') {
            console.warn('[App] Preload timeout (3min), falling back to no denoise/VAD');
            pipelineCapRef.current = { denoise: false, vad: false };
            return 'fallback';
          }
          return prev;
        });
      }
    }, 3 * 60 * 1000);

    AudioPipeline.preload((info) => {
      if (!cancelled) {
        setLoadProgress({ loaded: info.loaded, total: info.total, stage: info.stage });
      }
    }).then((result) => {
      if (!cancelled) {
        pipelineCapRef.current = result;
        clearTimeout(fallbackTimer);
        if (result.denoise && result.vad) {
          setPipelineState('ready');
        } else {
          // 部分加载失败，也算降级就绪
          setPipelineState(result.denoise || result.vad ? 'ready' : 'fallback');
        }
      }
    }).catch(() => {
      if (!cancelled) {
        clearTimeout(fallbackTimer);
        pipelineCapRef.current = { denoise: false, vad: false };
        setPipelineState('fallback');
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
    };
  }, []);

  // 录音时定期读取管线诊断数据
  useEffect(() => {
    if (!isRecording) { setPipelineStats(null); return; }
    const timer = setInterval(() => {
      if (pipelineRef.current) {
        setPipelineStats({ ...pipelineRef.current.stats });
      }
    }, 500);
    return () => clearInterval(timer);
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setStatus('connecting');
      setResults([]);
      subtitlesRef.current = [];
      audioChunksRef.current = [];

      // 在用户手势链中获取麦克风（移动端必须在点击事件中发起）
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 48000 }
      }).catch(err => {
        throw new Error('无法访问麦克风: ' + err.message);
      });
      streamRef.current = stream;

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.NODE_ENV === 'development'
        ? `${window.location.hostname}:8000`
        : window.location.host;
      
      const wsEndpoint = `/api/audio/stream`;
      const wsUrl = `${wsProtocol}//${wsHost}${wsEndpoint}?source_lang=${sourceLang}&target_lang=${targetLang}&model=${model}`;

      console.log(`[Recording] Starting, endpoint: ${wsEndpoint}`);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        setStatus('recording');
        recordingStartTimeRef.current = Date.now();

        try {
          // AudioPipeline (RNNoise 降噪 + Silero VAD + WebM/Opus)
          const cap = pipelineCapRef.current;
          const pipeline = new AudioPipeline({
            onAudioData: (data) => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(data);
              }
            },
            onVadChange: (speaking) => {
              setIsSpeaking(speaking);
            },
            onError: (msg) => {
              console.error('[Pipeline]', msg);
            },
            enableDenoise: cap.denoise,
            enableVad: cap.vad,
          });
          pipelineRef.current = pipeline;
          await pipeline.start(stream);

          setIsRecording(true);
        } catch (err) {
          setError('管线启动失败: ' + err.message);
          ws.close();
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'error') {
          setError(data.message);
          return;
        }

        // 处理费用统计
        if (data.usage) {
          setUsage(prev => {
            // segment_complete 格式：{ whisper: {...}, translate: {...}, total_cost_usd }
            if (data.usage.total_cost_usd !== undefined) {
              return {
                totalTokens: prev.totalTokens + (data.usage.translate?.total_tokens || 0),
                totalCost: prev.totalCost + (data.usage.total_cost_usd || 0),
                whisperSeconds: (prev.whisperSeconds || 0) + (data.usage.whisper?.duration_seconds || 0)
              };
            }
            // interim/final 格式：{ total_tokens, cost_usd }
            return {
              ...prev,
              totalTokens: prev.totalTokens + (data.usage.total_tokens || 0),
              totalCost: prev.totalCost + (data.usage.cost_usd || 0)
            };
          });
        }

        // 如果后端传回了 STT 时间戳（毫秒），优先使用；否则使用本地接收时间
        const timeOffset = (data.stt_offset !== undefined && data.stt_offset > 0)
          ? data.stt_offset
          : (Date.now() - recordingStartTimeRef.current);

        if (data.type === 'interim') {
          setInterimText(data.transcript);
          if (data.translated) {
            setInterimTranslated(data.translated);
            setCurrentResult({
              id: 'current',
              segmentId: data.segment_id,
              original: data.transcript,
              translated: data.translated,
              sourceLang: data.source_lang,
              targetLang: data.target_lang,
              timestamp: new Date().toLocaleTimeString(),
              isLive: true
            });
          }
        } else if (data.type === 'final') {
          console.log('[FINAL]', data.segment_id, data.transcript?.slice(0, 30));
          setInterimText('');
          setInterimTranslated('');
          if (data.transcript && data.transcript.trim()) {
            setCurrentResult(null);
            const subtitle = {
              id: Date.now(),
              segmentId: data.segment_id,
              timeOffset,
              original: data.transcript,
              translated: data.translated || data.transcript,
              sourceLang: data.source_lang,
              targetLang: data.target_lang,
              timestamp: new Date().toLocaleTimeString()
            };
            // 同步更新 subtitlesRef，只替换为更长的结果（更完整）
            const existingSubIndex = subtitlesRef.current.findIndex(s => s.segmentId === data.segment_id);
            if (existingSubIndex >= 0) {
              // 只有新结果更长时才替换，防止异步返回顺序问题
              if (data.transcript.length >= subtitlesRef.current[existingSubIndex].original.length) {
                subtitlesRef.current[existingSubIndex] = subtitle;
              }
            } else {
              subtitlesRef.current.push(subtitle);
            }
            // 检查是否已有相同 segment_id 的结果，只替换为更长的结果
            setResults(prev => {
              const existingIndex = prev.findIndex(r => r.segmentId === data.segment_id);
              if (existingIndex >= 0) {
                // 只有新结果更长时才替换
                if (data.transcript.length >= prev[existingIndex].original.length) {
                  const newResults = [...prev];
                  newResults[existingIndex] = subtitle;
                  return newResults;
                }
                // 新结果更短，忽略
                return prev;
              }
              // 新的 segment，追加到前面
              return [subtitle, ...prev];
            });
          }
        } else if (data.type === 'segment_complete') {
          // 段落完整识别结果：替换该 segment_id 下的所有分段结果
          const segmentId = data.segment_id;
          console.log('[segment_complete]', segmentId, data.transcript?.slice(0, 30));

          // 更新 results：移除该 segment 的所有旧结果，添加新的完整结果
          setResults(prev => {
            const otherResults = prev.filter(r => r.segmentId !== segmentId);
            // 找到该 segment 最早的 timeOffset
            const segmentResults = prev.filter(r => r.segmentId === segmentId);
            const earliestOffset = segmentResults.length > 0
              ? Math.min(...segmentResults.map(r => r.timeOffset))
              : timeOffset;

            const completeResult = {
              id: Date.now(),
              segmentId: segmentId,
              timeOffset: earliestOffset,
              original: data.transcript,
              translated: data.translated || data.transcript,
              sourceLang: data.source_lang,
              targetLang: data.target_lang,
              timestamp: new Date().toLocaleTimeString(),
              isComplete: true  // 标记为完整识别结果
            };

            // 按 timeOffset 排序（最新的在前）
            return [completeResult, ...otherResults].sort((a, b) => b.timeOffset - a.timeOffset);
          });

          // 同步更新 subtitlesRef（先找到 timeOffset 再过滤）
          const oldSegmentSubtitles = subtitlesRef.current.filter(s => s.segmentId === segmentId);
          const earliestSubOffset = oldSegmentSubtitles.length > 0
            ? Math.min(...oldSegmentSubtitles.map(s => s.timeOffset))
            : timeOffset;

          // 移除旧的分段字幕，添加完整版本
          subtitlesRef.current = subtitlesRef.current.filter(s => s.segmentId !== segmentId);
          subtitlesRef.current.push({
            id: Date.now(),
            segmentId: segmentId,
            timeOffset: earliestSubOffset,
            original: data.transcript,
            translated: data.translated || data.transcript,
            sourceLang: data.source_lang,
            targetLang: data.target_lang,
            isComplete: true
          });
        }
      };

      ws.onerror = () => {
        setError('连接错误');
        setStatus('idle');
        // 连接失败时释放麦克风
        if (streamRef.current && !pipelineRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
        }
      };

      ws.onclose = () => {
        setStatus('idle');
        setIsRecording(false);
      };

    } catch (err) {
      setError(err.message || '启动失败');
      setStatus('idle');
      // 出错时释放麦克风
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    }
  }, [sourceLang, targetLang, model]);

  const stopRecording = useCallback(async () => {
    const duration = Date.now() - (recordingStartTimeRef.current || Date.now());

    // 经典模式：停止 AudioPipeline（管理降噪、VAD、MediaRecorder、fullRecorder）
    if (pipelineRef.current) {
      const result = await pipelineRef.current.stop();
      audioChunksRef.current = result.audioChunks || [];
      pipelineRef.current = null;
    }

    // 停止流式 recorder（兜底）
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (wsRef.current) {
      wsRef.current.close();
    }

    setIsRecording(false);
    setIsSpeaking(false);
    setStatus('idle');
    setInterimText('');
    setInterimTranslated('');

    // 固定当前结果
    setCurrentResult(prev => {
      if (prev && prev.translated) {
        const subtitle = { ...prev, id: Date.now(), isLive: false, timeOffset: duration };
        subtitlesRef.current.push(subtitle);
        setResults(r => [subtitle, ...r]);
      }
      return null;
    });

    // 等待完整录音数据
    await new Promise(resolve => setTimeout(resolve, 500));

    // 保存录音
    if (audioChunksRef.current.length > 0 && subtitlesRef.current.length > 0) {
      try {
        setStatus('saving');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // 按 timeOffset 排序字幕，确保播放时顺序正确
        const sortedSubtitles = [...subtitlesRef.current].sort((a, b) => a.timeOffset - b.timeOffset);
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        formData.append('subtitles', JSON.stringify(sortedSubtitles));
        formData.append('source_lang', sourceLang);
        formData.append('target_lang', targetLang);
        formData.append('model', model);
        formData.append('duration_ms', duration.toString());

        const res = await fetch(`${API_BASE}/api/recordings/save`, {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          loadRecordings();
        }
        setStatus('idle');
      } catch (err) {
        console.error('保存录音失败:', err);
        setStatus('idle');
      }
    }
  }, [isRecording, sourceLang, targetLang, model, loadRecordings]);

  const clearResults = () => {
    setResults([]);
    subtitlesRef.current = [];
  };

  const getLangName = (code) => {
    const lang = LANGUAGES.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  // 播放录音
  const playRecording = async (recording) => {
    try {
      const res = await fetch(`${API_BASE}/api/recordings/${recording.id}`);
      const data = await res.json();
      setSelectedRecording({
        ...data,
        audioUrl: `${API_BASE}/api/recordings/${recording.id}/audio`
      });
      setCurrentSubtitleIndex(-1);
      setView('playback');
    } catch (err) {
      console.error('Failed to load recording:', err);
    }
  };

  // 删除录音
  const deleteRecording = async (id) => {
    if (!window.confirm('确定要删除这个录音吗？')) return;
    try {
      await fetch(`${API_BASE}/api/recordings/${id}`, { method: 'DELETE' });
      loadRecordings();
    } catch (err) {
      console.error('Failed to delete recording:', err);
    }
  };

  // 音频时间更新时同步字幕
  const handleTimeUpdate = () => {
    if (!audioPlayerRef.current || !selectedRecording?.subtitles) return;
    const currentTime = audioPlayerRef.current.currentTime * 1000;
    const subtitles = selectedRecording.subtitles;

    let newIndex = -1;
    for (let i = subtitles.length - 1; i >= 0; i--) {
      if (currentTime >= subtitles[i].timeOffset) {
        newIndex = i;
        break;
      }
    }
    setCurrentSubtitleIndex(newIndex);
  };

  // 点击字幕跳转
  const seekToSubtitle = (timeOffset) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = timeOffset / 1000;
    }
  };

  // 录音视图
  const renderRecordView = () => (
    <>
      <section className="controls">
        <div className="control-group">
          <label>说话语言:</label>
          <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} disabled={isRecording}>
            {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
          </select>
        </div>
        <div className="control-group">
          <label>翻译成:</label>
          <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} disabled={isRecording}>
            {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
          </select>
        </div>
        <div className="control-group">
          <label>翻译模型:</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} disabled={isRecording}>
            {MODELS.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </section>

      <section className="record-section">
        {/* 预加载状态提示 */}
        {pipelineState === 'loading' && (
          <div className="preload-status">
            <div className="preload-spinner" />
            <span className="preload-text">
              {loadProgress.stage === 'denoise' ? '正在加载降噪模块...' :
               loadProgress.stage === 'vad' ? '正在加载语音检测模块...' :
               loadProgress.stage === 'downloading'
                ? `正在下载资源 (${loadProgress.loaded}/${loadProgress.total})...`
                : '正在初始化音频引擎...'}
            </span>
            <div className="preload-bar">
              <div
                className="preload-bar-fill"
                style={{ width: `${Math.round((loadProgress.loaded / loadProgress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {pipelineState === 'fallback' && (
          <div className="preload-fallback-notice">
            ⚠ 降噪 / 语音检测模块加载失败，录音仍可使用，但无降噪效果
          </div>
        )}

        <button
          className={`record-btn ${isRecording ? 'recording' : ''}`}
          onClick={isRecording ? stopRecording : startRecording}
          disabled={pipelineState === 'loading' || status === 'connecting' || status === 'saving'}
        >
          {pipelineState === 'loading' ? '⏳ 引擎加载中...' :
            status === 'connecting' ? '连接中...' :
            status === 'saving' ? '保存中...' :
              isRecording ? '⏹ 停止' : '🎤 开始录音'}
        </button>

        {isRecording && (
          <div className="pipeline-status">
            {pipelineCapRef.current.vad && (
              <span className={`vad-indicator ${isSpeaking ? 'speaking' : 'silent'}`}>
                {isSpeaking ? '🗣 语音中' : '🔇 静音'}
              </span>
            )}
            {pipelineCapRef.current.denoise && (
              <span className="denoise-badge">🔉 降噪</span>
            )}
            {!pipelineCapRef.current.denoise && !pipelineCapRef.current.vad && (
              <span className="denoise-badge fallback-badge">⚠ 直通模式</span>
            )}
            {pipelineStats && (
              <span className="stats-badge">
                📦 {pipelineStats.chunksSent}/{pipelineStats.chunksProduced}
              </span>
            )}
          </div>
        )}

        {error && <div className="error">{error}</div>}
      </section>

      {interimText && (
        <section className="interim-section">
          <div className="interim-text">
            <div className="interim-original">{interimText}</div>
            {interimTranslated && <div className="interim-translated">{interimTranslated}</div>}
          </div>
        </section>
      )}

      <section className="results-section">
        <div className="results-header">
          <h2>翻译结果</h2>
          {results.length > 0 && <button className="clear-btn" onClick={clearResults}>清空</button>}
        </div>
        <div className="results-list">
          {!currentResult && results.length === 0 ? (
            <div className="empty">点击录音按钮开始，说完一句话后会自动翻译...</div>
          ) : (
            <>
              {currentResult && (
                <div className="result-item live">
                  <div className="result-meta">
                    <span className="lang-badge live-badge">{getLangName(currentResult.sourceLang)} → {getLangName(currentResult.targetLang)} 🔴</span>
                    <span className="timestamp">{currentResult.timestamp}</span>
                  </div>
                  <div className="result-original"><label>原文:</label><p>{currentResult.original}</p></div>
                  <div className="result-translated"><label>译文:</label><p>{currentResult.translated}</p></div>
                </div>
              )}
              {results.map(result => (
                <div key={result.id} className={`result-item ${result.isComplete ? 'complete' : ''}`}>
                  <div className="result-meta">
                    <span className="lang-badge">
                      {getLangName(result.sourceLang)} → {getLangName(result.targetLang)}
                      {result.isComplete && <span className="complete-badge"> ✓</span>}
                    </span>
                    <span className="timestamp">{result.timestamp}</span>
                  </div>
                  <div className="result-original"><label>原文:</label><p>{result.original}</p></div>
                  <div className="result-translated"><label>译文:</label><p>{result.translated}</p></div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>
    </>
  );

  // 获取模型显示名称
  const getModelDisplayName = (modelId) => {
    const m = MODELS.find(m => m.id === modelId);
    return m ? m.name.split(' ')[0] : modelId;
  };

  // 历史记录视图
  const renderHistoryView = () => (
    <section className="history-section">
      <h2>录音历史</h2>
      {recordings.length === 0 ? (
        <div className="empty">暂无录音记录</div>
      ) : (
        <div className="recordings-list">
          {recordings.map(rec => (
            <div key={rec.id} className="recording-item">
              <div className="recording-info">
                <span className="recording-date">{new Date(rec.created_at).toLocaleString()}</span>
                <span className="recording-meta">
                  {getLangName(rec.source_lang)} → {getLangName(rec.target_lang)} | {rec.subtitle_count} 句 | {Math.round(rec.duration_ms / 1000)}秒
                  <span className="recording-model"> | {getModelDisplayName(rec.model)}</span>
                </span>
              </div>
              <div className="recording-actions">
                <button className="play-btn" onClick={() => playRecording(rec)}>▶ 播放</button>
                <button className="delete-btn" onClick={() => deleteRecording(rec.id)}>删除</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  // 播放视图
  const renderPlaybackView = () => (
    <section className="playback-section">
      <button className="back-btn" onClick={() => { setView('history'); setSelectedRecording(null); }}>← 返回</button>

      {selectedRecording && (
        <>
          <div className="audio-player">
            <audio
              ref={audioPlayerRef}
              src={selectedRecording.audioUrl}
              controls
              onTimeUpdate={handleTimeUpdate}
              style={{ width: '100%' }}
            />
          </div>

          <div className="playback-subtitles">
            {selectedRecording.subtitles?.map((sub, index) => (
              <div
                key={sub.id || index}
                className={`subtitle-item ${index === currentSubtitleIndex ? 'active' : ''}`}
                onClick={() => seekToSubtitle(sub.timeOffset)}
              >
                <span className="subtitle-time">{Math.floor(sub.timeOffset / 1000)}s</span>
                <div className="subtitle-content">
                  <p className="subtitle-original">{sub.original}</p>
                  <p className="subtitle-translated">{sub.translated}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );

  return (
    <div className="app">
      <header className="header">
        <h1>🌐 Lang Assistant</h1>
        <p>实时流式语音翻译 - 中/英/日</p>
        {(usage.totalTokens > 0 || usage.totalCost > 0) && (
          <div className="usage-stats">
            <span>Tokens: {usage.totalTokens.toLocaleString()}</span>
            {usage.whisperSeconds > 0 && <span>Whisper: {Math.round(usage.whisperSeconds)}s</span>}
            <span>费用: ${usage.totalCost.toFixed(4)}</span>
          </div>
        )}
        <nav className="nav-tabs">
          <button className={view === 'record' ? 'active' : ''} onClick={() => setView('record')}>录音翻译</button>
          <button className={view === 'history' ? 'active' : ''} onClick={() => { setView('history'); loadRecordings(); }}>历史记录</button>
        </nav>
      </header>

      <main className="main">
        {view === 'record' && renderRecordView()}
        {view === 'history' && renderHistoryView()}
        {view === 'playback' && renderPlaybackView()}
      </main>
    </div>
  );
}

export default App;
